/**
 * Carte « Mon année » du tableau de bord élève (lot F) : le pack en cours de
 * la progression de son niveau et sa maîtrise de l'année → /student/progression.
 * Rien tant qu'aucun pack n'est publié pour son niveau.
 *
 * Un pied de carte mène à la FICHE DE RÉVISION du pack en cours (0723). Sans lui,
 * la fiche n'était atteignable qu'en dépliant un pack dans « Mon année » : personne
 * ne la trouvait depuis l'accueil.
 *
 * La carte n'est donc plus un `<Link>` englobant — deux liens imbriqués ne sont pas
 * du HTML valide. Le haut reste cliquable, le pied est un lien à part.
 */

import { Link } from '@tanstack/react-router';
import { CalendarRange, ChevronRight, FileText } from 'lucide-react';
import { PackIconGlyph } from '@jeraisonne/ui';
import { levelLabel } from '@jeraisonne/db/curriculum';
import { useMyProgression } from '@/lib/curriculum/api';
import { yearSummary } from '@/lib/curriculum/progress';

export function MyYearCard() {
  const progression = useMyProgression();
  const packs = progression.data?.packs ?? [];
  if (progression.isLoading || progression.error || packs.length === 0) return null;
  const summary = yearSummary(packs);
  const current = summary.current;

  return (
    <div
      className="border-ink-200 hover:border-primary-300 rounded-2xl border bg-white"
      data-testid="my-year-card"
    >
      <Link to="/student/progression" className="block p-4 active:scale-[0.99]">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display text-ink-900 flex items-center gap-2 text-lg font-bold">
            <CalendarRange className="text-primary-500 h-5 w-5" aria-hidden />
            Mon année{progression.data?.level ? ` · ${levelLabel(progression.data.level)}` : ''}
          </h2>
          <ChevronRight className="text-ink-400 h-5 w-5" aria-hidden />
        </div>
        {current && (
          <div className="mt-3 flex items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: `${current.color ?? '#4DA3FF'}22`,
                color: current.color ?? '#4DA3FF',
              }}
            >
              <PackIconGlyph icon={current.icon} className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-ink-500 text-xs font-semibold uppercase">En ce moment</p>
              <p className="text-ink-900 truncate font-semibold">{current.title}</p>
            </div>
          </div>
        )}
        <div className="mt-3">
          <div className="bg-ink-100 h-2 overflow-hidden rounded-full">
            <div
              className="bg-primary-500 h-full rounded-full"
              style={{ width: `${summary.percent ?? 0}%` }}
            />
          </div>
          <p className="text-ink-600 mt-1 text-xs">
            {summary.percent ?? 0} % des SKILLCARDS de l’année maîtrisées · {summary.hora} HORA
          </p>
        </div>
      </Link>

      {current && current.card_count > 0 && (
        <Link
          to="/pack/$packId/fiche"
          params={{ packId: current.id }}
          search={{ student: undefined }}
          className="border-ink-100 text-primary-700 hover:bg-primary-50 flex min-h-[2.75rem] items-center gap-1.5 border-t px-4 py-2 text-sm font-semibold"
          data-testid="my-year-fiche"
        >
          <FileText className="h-4 w-4" aria-hidden /> Fiche de révision du pack
        </Link>
      )}
    </div>
  );
}
